import { useEffect, useState, MouseEvent, Fragment } from "react";
import {
    ShowBase,
    ReferenceOneField,
    useShowContext,
    useRecordContext,
    useNotify,
    RecordContextProvider
} from "react-admin";
import {
    Box, Card, CardContent, CircularProgress, Stack, Typography, Divider, Grid
} from "@mui/material";

import { useDataProvider } from "react-admin";

import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import PaymentIcon from "@mui/icons-material/Payment";
import RedeemIcon from "@mui/icons-material/Payments"; // Reward
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from "@mui/icons-material/Today";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"; // Import arrow icon
import LocalAtmIcon from "@mui/icons-material/LocalAtm"; // Import arrow icon
import PriceChangeIcon from "@mui/icons-material/PriceChangeOutlined"; // Import arrow icon
import TagIcon from "@mui/icons-material/Numbers";
import AddCardIcon from "@mui/icons-material/AddCard";
import DownloadIcon from "@mui/icons-material/DownloadDone";


import { TransactionAside } from "./TransactionAside";
import { Fund, Transaction, Settlement, Valuation } from "../../types/types";
import { useGetOne } from "react-admin";

// Helper function to format timestamps
const formatDate = (dateStr: string | undefined) =>
    dateStr ? new Date(dateStr).toLocaleString() : "N/A";

function formatTimestamp(timestamp: number | string | Date): string {
    const date = new Date(timestamp);

    const day: string = String(date.getDate()).padStart(2, '0');
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const year: string = String(date.getFullYear()).slice(-2);

    const hours: string = String(date.getHours()).padStart(2, '0');
    const minutes: string = String(date.getMinutes()).padStart(2, '0');
    const seconds: string = String(date.getSeconds()).padStart(2, '0');

    return `${day}.${month}.${year} - ${hours}:${minutes}:${seconds}`;
}

const capitalizeFirstLetter = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const statusIcons = {
    pending: <HourglassTopIcon sx={{ verticalAlign: "middle" }} color="primary" />,
    completed: <CheckCircleIcon sx={{ verticalAlign: "middle" }} color="success" />,
    canceled: <CancelIcon sx={{ verticalAlign: "middle" }} color="error" />
};

const transferIcons = {
    deposit: <CallMadeIcon fontSize="small" sx={{ verticalAlign: "middle" }} color="primary" />,
    withdrawal: <CallReceivedIcon fontSize="small" sx={{ verticalAlign: "middle" }} color="primary" />,
    reward: <RedeemIcon fontSize="small" sx={{ verticalAlign: "middle" }} color="primary" />,
};

export const TransactionShow = () => (
    <ShowBase>
        <TransactionShowContent />
    </ShowBase>
);

const TransactionShowContent = () => {
    const { record, isPending } = useShowContext<Transaction>();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [entityNames, setEntityNames] = useState<Record<string, string>>({});
    const [fund, setFund] = useState<Fund>();

    const {
        data: paymentRaw,
        isLoading: isPaymentLoading,
    } = useGetOne("payments", { id: record?.transaction_id }, {
        enabled: !!record?.transaction_id,
    });

    const payment =
        paymentRaw &&
            Object.keys(paymentRaw).length > 1 &&
            paymentRaw.transaction_id === record?.transaction_id
            ? paymentRaw
            : undefined;

    const {
        data: settlementRaw,
        isLoading: isSettlementLoading,
    } = useGetOne("settlements", { id: record?.transaction_id }, {
        enabled: !!record?.transaction_id && ["withdrawal", "reward"].includes(record.transfer_type),
    });

    const settlement =
        settlementRaw &&
            Object.keys(settlementRaw).length > 1 &&
            settlementRaw.transaction === record?.transaction_id
            ? settlementRaw
            : undefined;

    const handleCopy = (e: MouseEvent<HTMLSpanElement>, transactionId: string): void => {
        e.stopPropagation();
        e.preventDefault();
        navigator.clipboard.writeText(transactionId);
        notify("Transaction ID copied!");
    };

    useEffect(() => {
        if (!record) return;

        const names: Record<string, string> = {};

        Promise.all([
            dataProvider.getOne("users", { id: record.user_id })
                .then(({ data }) => {
                    if (data?.first_name && data?.last_name) {
                        names[record.user_id] = `${data.first_name} ${data.last_name}`;
                    }
                })
                .catch(() => {
                    names[record.user_id] = record.user_id;
                }),

            dataProvider.getOne("funds", { id: record.asset_id })
                .then(({ data }) => {
                    if (data?.name) {
                        names[record.asset_id] = data.name;
                        setFund(data);
                    }
                })
                .catch(() => {
                    names[record.asset_id] = record.asset_id;
                })
        ]).then(() => {
            setEntityNames(names);
        });
    }, [record, dataProvider]);

    if (isPending || !record || isPaymentLoading || Object.keys(entityNames).length === 0) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box mt={2} mb={2} display="flex">
            <Box flex="1">
                <Card sx={{ p: 3 }}>
                    <Grid container alignItems="center" sx={{ mb: 2 }}>
                        <Grid item sx={{ display: "flex", alignItems: "center" }}>
                            <TagIcon sx={{ mr: 1 }} />
                            <Typography variant="h5"
                                onClick={(e) => handleCopy(e, record.transaction_id)}
                                sx={{ cursor: "pointer", userSelect: "text" }}>
                                {record.transaction_id}
                            </Typography>
                        </Grid>
                        <Grid item xs />
                        <Grid item>
                            {statusIcons[record.status]}
                        </Grid>
                    </Grid>


                    <Divider sx={{ my: 2 }} />

                    {/* Transaction Details Section */}

                    <Card variant="outlined">
                        <CardContent>
                            <Grid container alignItems="center" spacing={2}>
                                {/* Transfer Type */}
                                <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
                                    {transferIcons[record.transfer_type]}
                                    <Typography align="left" sx={{ ml: 1 }}>
                                        {capitalizeFirstLetter(record.transfer_type)}
                                    </Typography>
                                </Grid>

                                {/* Timestamp */}
                                <Grid item xs={9.5} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                    <CalendarTodayIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                                    <Typography align="left" variant="body2">
                                        {formatTimestamp(record.timestamp)}
                                    </Typography>
                                </Grid>

                                {/* Status Icon (aligned properly) */}
                                <Grid item xs={0.5} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                    {statusIcons[record.status]}
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 1, mb: 3 }} />


                            {/* First Row: From → Shares & Value → To */}
                            <Grid container alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                {/* From */}
                                <Grid item xs={5.5} textAlign="center">
                                    <Typography>{entityNames[record.source] || record.source}</Typography>
                                </Grid>

                                {/* Middle Column: Shares & Value */}
                                <Grid item xs={1} textAlign="center">
                                    <ArrowForwardIcon fontSize="medium" color="primary" sx={{ mt: 0.5 }} />
                                </Grid>

                                {/* To */}
                                <Grid item xs={5.5} textAlign="center">
                                    <Typography><strong>{entityNames[record.target] || record.target}</strong></Typography>
                                </Grid>
                            </Grid>

                            <Grid container alignItems="center" spacing={2} >

                                <Grid item xs={2} textAlign="center">
                                </Grid>

                                {/* Middle Column: Shares & Value */}
                                <Grid item xs={8}>
                                    <Grid container spacing={0.5} justifyContent="center">
                                        {/* Shares */}
                                        {(record.transfer_type === "withdrawal" || record.status === "completed") && (
                                            <>
                                                <Grid item xs={6} textAlign="right">
                                                    <Typography variant="body2">Shares:</Typography>
                                                </Grid>
                                                <Grid item xs={6} textAlign="left">
                                                    <Typography variant="body2"><strong>{record.amount.toFixed(2)}</strong> </Typography>
                                                </Grid>
                                            </>
                                        )}

                                        {/* Amount */}
                                        {(record.transfer_type === "deposit" || record.status === "completed") && (
                                            <>
                                                <Grid item xs={6} textAlign="right">
                                                    <Typography variant="body2">Amount:</Typography>
                                                </Grid>
                                                <Grid item xs={6} textAlign="left">
                                                    <Typography variant="body2"><strong>{record.valuation['EUR']?.toFixed(2)} €</strong></Typography>
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>


                                <Grid item xs={2} textAlign="center">
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {record.transfer_type === "deposit" && record.status === "pending" && (
                        <FundingDetails record={record} fund={fund} />
                    )}

                    {/* Settlement Details */}
                    {settlement ? (
                        <RecordContextProvider value={settlement}>
                            <SettlementDetails entityNames={entityNames} />
                        </RecordContextProvider>
                    ) : record.transfer_type !== "deposit" && record.status == "pending" ? (
                        <Card variant="outlined" sx={{ mt: 2 }}>
                            <CardContent>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
                                        <DownloadIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                                        <Typography align="left">Settlement</Typography>
                                    </Grid>
                                    <Grid item xs={10} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                        <HourglassTopIcon color="primary" />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 1, mb: 3 }} />

                                <Typography variant="body2" color="text.secondary" align="center">
                                    The price per share and respective withdrawal amount will be processed automatically <br></br> by our system around midnight UTC.
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : null}

                    {/* Payment Details */}
                    {payment ? (
                        <RecordContextProvider value={payment}>
                            <PaymentDetails />
                        </RecordContextProvider>
                    ) : record.transfer_type !== "deposit" && record.status == "pending" ? (
                        <Card variant="outlined" sx={{ mt: 2 }}>
                            <CardContent>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
                                        <PaymentIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                                        <Typography align="left">Payment</Typography>
                                    </Grid>
                                    <Grid item xs={10} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                        <HourglassTopIcon color="primary" />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 1, mb: 3 }} />

                                <Typography variant="body2" color="text.secondary" align="center">
                                    The payment will be processed once the settlement is completed.
                                </Typography>

                            </CardContent>
                        </Card>
                    ) : null}

                    {record.status === "completed" && (
                        <ReferenceOneField source="transaction_id" reference="statements" target="transaction">
                            <StatementDetails />
                        </ReferenceOneField>
                    )}
                </Card>
            </Box>
            <TransactionAside />
        </Box>
    );
};

function formatIban(iban: string): string {
    return iban
        .replace(/\s+/g, '')        // Remove any existing spaces
        .replace(/(.{4})/g, '$1 ')  // Insert a space every 4 chars
        .trim();                    // Remove trailing space
}

/* Payment Details Component */
const PaymentDetails = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
                <Grid container alignItems="center" spacing={2}>
                    {/* Payment Label */}
                    <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
                        <PaymentIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                        <Typography align="left">Payment</Typography>
                    </Grid>

                    {/* Date aligned to the right */}
                    <Grid item xs={9.5} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        <CalendarTodayIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                        <Typography align="left" variant="body2">{formatTimestamp(record.creation_date)}</Typography>
                    </Grid>

                    {/* Status Icon (aligned properly) */}
                    <Grid item xs={0.5} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        {statusIcons[record.status as keyof typeof statusIcons]}
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1, mb: 3 }} />


                {/* Payment Route: Sender → Recipient */}
                <Grid container alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={5.5} textAlign="center">
                        <Typography>{record.sender_name}</Typography>
                        <Typography variant="body2" color="textSecondary">{formatIban(record.sender_account)}</Typography>
                        <Typography variant="body2" color="textSecondary">{record.sender_bank_name} ({record.sender_bic})</Typography>
                    </Grid>
                    <Grid item xs={1} textAlign="center">
                        <ArrowForwardIcon fontSize="medium" color="primary" sx={{ mt: 0.5 }} />
                    </Grid>
                    <Grid item xs={5.5} textAlign="center">
                        <Typography><strong>{record.recipient_name}</strong></Typography>
                        <Typography variant="body2" color="textSecondary">{formatIban(record.recipient_account)}</Typography>
                        <Typography variant="body2" color="textSecondary">{record.recipient_bank_name} ({record.recipient_bic})</Typography>
                    </Grid>
                </Grid>

                {/* Payment Amount & Status */}
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={2}></Grid>

                    <Grid item xs={8}>
                        <Grid container spacing={1} justifyContent="center">
                            <>
                                <Grid item xs={6} textAlign="right">
                                    <Typography variant="body2">Amount:</Typography>
                                </Grid>
                                <Grid item xs={6} textAlign="left">
                                    <Typography variant="body2">
                                        <strong>{record.amount.toFixed(2)} €</strong>
                                    </Typography>
                                </Grid>
                            </>
                        </Grid>
                    </Grid>

                    <Grid item xs={2}></Grid>
                </Grid>

            </CardContent>
        </Card>
    );
};

type SettlementDetailsProps = {
    entityNames: Record<string, string>;
};

const SettlementDetails = ({ entityNames }: SettlementDetailsProps) => {
    const record = useRecordContext<Settlement>();
    if (!record) return null;

    const formatValue = (val: Valuation) => val?.global.EUR?.toFixed(2) + ' €' || 'N/A';
    const formatAmount = (num: number) => num?.toFixed(2);

    const grossPnl = record.realized_pnl?.global.EUR || 0;
    const fee = record.fees?.global.EUR || 0;
    const netPnl = grossPnl - fee;

    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
                {/* Header */}
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
                        <DownloadIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                        <Typography>Settlement</Typography>
                    </Grid>
                    <Grid item xs={9.5} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        <CalendarTodayIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2">{formatTimestamp(record.timestamp)}</Typography>
                    </Grid>
                    <Grid item xs={0.5} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <CheckCircleIcon color="success" />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={1} alignItems="center">
                    {[
                        { label: "Fund", value: entityNames[record.asset_id] },
                        { label: "Amount", value: formatAmount(record.amount) },
                        { label: "Cost Basis", value: formatValue(record.cost_basis) },
                        { label: "Valuation", value: formatValue(record.valuation) },
                    ].map((item, index) => (
                        <Fragment key={index}>
                            <Grid item xs={3}>
                                <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="body2" fontWeight={500}>{item.value}</Typography>
                            </Grid>
                        </Fragment>
                    ))}

                    {/* Profit & Loss Section */}
                    <Grid item xs={12} sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>Profit & Loss</Typography>
                        <Divider sx={{ mb: 1 }} />
                    </Grid>

                    {[
                        { label: "Gross Realized P&L", value: formatValue(record.realized_pnl) },
                        { label: "Fees", value: formatValue(record.fees) },
                        {
                            label: "Net Realized P&L",
                            value: (
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{ color: netPnl >= 0 ? "success.main" : "error.main" }}
                                >
                                    {netPnl.toFixed(2)} €
                                </Typography>
                            ),
                        },
                    ].map((item, index) => (
                        <Fragment key={index}>
                            <Grid item xs={3}>
                                <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                {typeof item.value === "string" ? (
                                    <Typography variant="body2" fontWeight={500}>{item.value}</Typography>
                                ) : (
                                    item.value
                                )}
                            </Grid>
                        </Fragment>
                    ))}

                    {/* Notes */}
                    {record.meta_data?.notes && (
                        <Grid item xs={12} sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">{record.meta_data.notes}</Typography>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};

/* Statement Details */
const StatementDetails = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6">Statement Details</Typography>
                <Divider sx={{ my: 1, mb: 3 }} />
                <Stack spacing={1}>
                    <Typography variant="body2"><strong>Type:</strong> {record.statement_type}</Typography>
                    <Typography variant="body2"><strong>Period:</strong> {record.statement_period}</Typography>
                    <Typography variant="body2"><strong>Amount:</strong> {JSON.stringify(record.amount)}</Typography>
                </Stack>
            </CardContent>
        </Card>
    );
};

const FundingDetails = ({ record, fund }: { record: Transaction; fund?: Fund; }) => (
    <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AddCardIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="h6">Funding</Typography>
            </Box>
            <Divider sx={{ my: 1, mb: 3 }} />

            {/* Bank & BIC on the same line */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={4}>
                    <Typography variant="body2"><strong>Bank:</strong> {fund?.bank || "N/A"}</Typography>
                </Grid>
                <Grid item xs={8}>
                    <Typography variant="body2"><strong>BIC:</strong> {fund?.bic || "N/A"}</Typography>
                </Grid>
            </Grid>

            {/* Rest of the funding details */}
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Typography variant="body2"><strong>Bank Account:</strong></Typography>
                    <Typography variant="body2"><strong>IBAN:</strong></Typography>
                    <Typography variant="body2"><strong>Reference Code:</strong></Typography>
                    <Typography variant="body2"><strong>Amount:</strong></Typography>
                </Grid>

                <Grid item xs={8}>
                    <Typography variant="body2">{fund?.bank_account || "N/A"}</Typography>
                    <Typography variant="body2">{fund?.iban || "N/A"}</Typography>
                    <Typography variant="body2">{record.id}</Typography>
                    <Typography variant="body2">{record.valuation['EUR'].toFixed(2)} EUR</Typography>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Information Text */}
            <Typography variant="body2" color="text.secondary">
                <strong>Important Information:</strong>
                <br /><br />
                Please make sure to <strong>transfer the exact specified amount in EUR within 14 days</strong>. If the payment is <strong>not received on time</strong> or <strong>the transferred amount does not match the required deposit</strong>, the deposit will be <strong>automatically canceled</strong>. Any incorrectly transferred funds will be <strong>returned within 5 business days</strong>.
                <br /><br />
                <strong>To ensure smooth processing, please use the correct reference code when making the payment.</strong>
                <br /><br />
                Only <strong>EUR (Euro)</strong> is accepted for funding. If you are sending funds from a non-EUR account, ensure that the target amount matches the exact deposit amount in EUR. Payments made in other currencies not matching the exact deposit amount when converted may be rejected or subject to processing delays and additional fees.
                <br /><br />
                Once the funds are transferred, <strong>the deposit is not processed instantly</strong>. Deposits are usually <strong>completed by midnight UTC</strong> on the day the payment is received.
                <br /><br />
                You may <strong>cancel your deposit at any time before the funds are transferred</strong>. However, <strong>after the funding is completed, cancellation is not guaranteed</strong>. If the deposit is canceled after funds have been received, the amount will be <strong>returned within 5 business days</strong>.

            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Additional Funding Information */}
            <Typography variant="body2" color="text.secondary">
                <strong>Determination of Shares & Price:</strong>
                <br /><br />
                The <strong>price per share</strong> and the <strong>number of shares</strong> you will receive are <strong>not fixed at the time of deposit</strong>.
                The final price will be determined when your capital is accounted for and processed based on the fund’s value at midnight UTC.
                <br /><br />
                Shares can only be allocated in <strong>full units</strong> and not in fractions.
                Any remaining funds that cannot be converted into a full share will be returned to you <strong>within five business days</strong>.
            </Typography>


        </CardContent>
    </Card>
);